import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { logout } from "../utility/Auth";
import LoginDialog from "../dialogs/LoginDialog";
import { auth } from "../firebaseConfigurations/config";
import GlowCanvasLogo from "./GlowCanvasLogo";
import { FiLogOut } from "react-icons/fi";
import { FaRocket } from "react-icons/fa";
import { useAuth } from "../context/AuthContext";

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const { isSubscribed } = useAuth();
  const dropdownRef = useRef(null);

  // Subscribe to Firebase Auth State
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDropdown]);

  const handleLogout = async () => {
    try {
      await logout();
      setUser(null);
      setMenuOpen(false);
    } catch (error) {
      alert("Logout failed: " + error.message);
    }
  };

  const toggleDropdown = () => {
    if (isSubscribed) {
      setShowDropdown(!showDropdown);
    }
  };

  return (
    <>
      <nav
        style={{
          width: "100%",
          padding: "12px 32px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: "#000",
          boxShadow: "0 1px 0 #333",
          position: "sticky",
          top: 0,
          zIndex: 100,
          minHeight: 60,
        }}
      >
        {/* Logo */}
        <Link
          to="/"
          style={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <GlowCanvasLogo />
        </Link>

        {/* Right Side Buttons */}
        <div
          className="nav-right"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            marginLeft: "auto",
            position: "relative",
            width: "auto",
          }}
        >
          <Link to="/pricing" className="nav-btn desktop-only right-btn">
            <div className="flex justify-center items-center gap-2">
              {" "}
              <span>Upgrade to Pro</span> <FaRocket size={18} color="#ff6600" />{" "}
            </div>
          </Link>

          {user ? (
            <>
              {/* Profile picture always visible, name hidden on mobile */}
              <div
                ref={dropdownRef}
                className="profile-container"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  cursor: "pointer",
                  userSelect: "none",
                  title: user.displayName || "User",
                  position: "relative",
                }}
                onClick={toggleDropdown}
              >
                <img
                  src={user.photoURL || "https://www.gravatar.com/avatar?d=mp"}
                  alt="Profile"
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    border: "2px solid #EEE",
                  }}
                />
                <span className="profile-name text-white" style={{ fontWeight: 500 }}>
                  {user.displayName
                    ? user.displayName.split(" ")[0]
                    : "Profile"}
                </span>

                {/* Dropdown for subscribed users */}
                {isSubscribed && showDropdown && (
                  <div
                    style={{
                      position: "absolute",
                      top: "45px",
                      right: 0,
                      background: "#1a1a1a",
                      borderRadius: "8px",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
                      padding: "8px",
                      minWidth: "180px",
                      zIndex: 1000,
                    }}
                  >
                    <a
                      href="https://gumroad.com/library"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: "block",
                        padding: "10px 12px",
                        color: "#fff",
                        textDecoration: "none",
                        borderRadius: "6px",
                        fontSize: "14px",
                        fontWeight: 500,
                        transition: "background 0.2s",
                      }}
                      onMouseEnter={(e) => e.target.style.background = "#333"}
                      onMouseLeave={(e) => e.target.style.background = "transparent"}
                    >
                      Manage Subscription
                    </a>
                  </div>
                )}
              </div>
              <button
                className="nav-btn right-btn desktop-only"
                onClick={handleLogout}
              >
                Logout
              </button>
            </>
          ) : (
            <button
              className="nav-btn login-top"
              style={{ marginLeft: 6 }}
              onClick={() => setLoginOpen(true)}
            >
              Login
            </button>
          )}

          {/* Burger for mobile */}
          <div
            className="burger"
            onClick={() => setMenuOpen(!menuOpen)}
            style={{ marginLeft: 0, position: "static" }}
          >
            <span
              style={{
                display: "block",
                width: 24,
                height: 3,
                background: "#fff",
                marginBottom: 5,
                borderRadius: 2,
              }}
            ></span>
            <span
              style={{
                display: "block",
                width: 24,
                height: 3,
                background: "#fff",
                marginBottom: 5,
                borderRadius: 2,
              }}
            ></span>
            <span
              style={{
                display: "block",
                width: 24,
                height: 3,
                background: "#fff",
                borderRadius: 2,
              }}
            ></span>
          </div>
        </div>

        {/* Burger menu for mobile */}
        <div
          className="nav-menu"
          style={{
            display: menuOpen ? "flex" : "none",
            flexDirection: "column",
            position: "absolute",
            top: 60,
            right: 24,
            background: "#1a1a1a",
            boxShadow: "0 2px 8px rgba(0,0,0,0.5)",
            borderRadius: 10,
            zIndex: 100,
            padding: 12,
            gap: 12,
          }}
        >
          <Link to="/pricing" className="nav-btn">
            <div className="flex justify-center items-center gap-2">
              {" "}
              <span>Upgrade to Pro</span> <FaRocket size={18} color="#ff6600" />{" "}
            </div>
          </Link>

          {user && (
            <button
              className="nav-btn"
              onClick={handleLogout}
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              <FiLogOut size={18} />
              Logout
            </button>
          )}
        </div>

        <style>{`
          .nav-btn {
            background: none;
            border: none;
            color: #fff;
            font-size: 16px;
            cursor: pointer;
            padding: 8px 16px;
            border-radius: 24px;
            font-weight: 600;
            transition: background 0.2s;
            text-decoration: none;
          }
          .nav-btn:hover {
            background: #333;
          }
          .create-btn {
            background: #FF822C;
            color: #fff;
            padding: 8px 22px;
            font-weight: 600;
          }
          .create-btn:hover {
            background: #ff983e;
          }
          .login-top {
            background: #000;
            color: #FF822C;
            padding: 8px 20px;
            border: 2px solid #FF822C;
            font-weight: 600;
            border-radius: 24px;
            transition: background 0.2s, color 0.2s;
            margin-left: 8px;
          }
          .login-top:hover {
            background: #FF822C;
            color: #fff;
          }
          @media (max-width: 900px) {
            .desktop-only {
              display: none !important;
            }
            .nav-right {
              width: 100%;
              justify-content: flex-end;
              position: relative;
            }
            .login-top {
              display: block !important;
              margin-right: 60px;
            }
            .profile-name {
              display: none;
            }
            .profile-container {
              margin-right: 50px; /* prevents burger overlap */
            }
            .burger {
              display: block !important;
              position: absolute !important;
              right: 16px !important;
              top: 50%;
              transform: translateY(-50%);
              margin: 0 !important;
              z-index: 110; /* on top */
            }
          }
          @media (min-width: 900px) {
            .burger,
            .nav-menu {
              display: none !important;
            }
            .desktop-only {
              display: inline-block !important;
            }
            .login-top {
              font-size: 1rem;
              margin-left: 12px;
              margin-right: 0;
            }
          }
        `}</style>
      </nav>

      {/* Login Dialog component */}
      <LoginDialog open={loginOpen} onClose={() => setLoginOpen(false)} />
    </>
  );
}
